import { useEffect, useState } from 'react';

import { IconZoomOutline } from '@iwoca/icons';
import { Card, Input, Spinner } from '@iwoca/orion';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';

import styles from './CompanySearch.module.css';
import { useGetCompanySearch } from '../../../../../api/companySearch/useGetCompanySearch';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

// DIT-680. Register search: debounced live lookup (300ms, min 3 chars) with an
// inline results list. Uses an inline list rather than the desktop
// CompanyInputSearchDE's Combobox, whose overlay conflicts with the mobile
// keyboard. Results are display-only; selection (DIT-681) and manual-entry
// fallback (DIT-682) are out of scope.
export const CompanySearch = () => {
  const { t } = useLingui();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.length >= MIN_QUERY_LENGTH ? query : '');
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching, isError } = useGetCompanySearch({
    searchQuery: debouncedQuery,
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
  });

  // On a failed lookup, show no results (matches desktop).
  const results = isError ? [] : (data?.data ?? []);
  const resultCount = results.length;
  const hasSearched = debouncedQuery.length >= MIN_QUERY_LENGTH;

  return (
    <div className={styles.searchScreen}>
      <Input
        iconLeft={<IconZoomOutline />}
        placeholder={t`e.g. Müller`}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        className="fs-mask"
      />
      {/* Visible status, also announced to screen readers as results update. */}
      {(isFetching || hasSearched) && (
        <div className={styles.searchStatus} role="status" aria-live="polite">
          {isFetching ? (
            <Spinner />
          ) : (
            plural(resultCount, {
              one: '# result',
              other: '# results',
            })
          )}
        </div>
      )}
      {!isFetching && results.length > 0 && (
        <ul className={styles.results}>
          {results.map((company) => (
            <li key={company.crefo_id}>
              <Card
                className={clsx(styles.result, 'fs-mask', 'dd-privacy-mask')}
              >
                <b>{company.name}</b> {company.street} {company.house_number},{' '}
                {company.postcode} {company.city}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
